<template>
  <div class="card" :class="{ 'card--vertical': !horizontal, 'card--horizontal': horizontal }" @click="useOpenWidget(restaurant.hash, meal.id, fat.amount, date)">
    <div class="card__wrapper">
      <div class="card__media">
        <div class="card__promoted" v-if="restaurant.promoted && restaurant.promoted?.promotionTypes?.includes('offers')">
          {{ restaurant.promoted?.text }}
        </div>
        <div class="card__media-overlay" v-if="restaurant.logo">
          <div class="card__media-logo" v-if="restaurant.logo">
            <AtomImage class="h-full !object-contain object-left-bottom" :src="useCloudinaryImage(restaurant.logo, 300, null, 'fit,e_trim')" :alt="restaurant.name" />
          </div>
          <div class="card__media-gradient"></div>
        </div>
        <AtomImage class="object-cover" :src="imageUrl" :alt="restaurant.name" />
      </div>
      <div class="card__main">
        <div class="card__label">
          <div class="card__discount">
            {{ discountText }}
          </div>
        </div>
        <h2 class="card__heading" v-html="restaurant.name" />
        <div class="card__description markdown" v-html="useMarked(restaurant.shortDescription)" v-if="restaurant.shortDescription" />
        <div class="card__meta" v-if="informationText">
          <div class="flex items-center gap-1">
            <AtomIcon name="circle-info" class="w-4" />
            <div v-html="informationText" />
          </div>
        </div>
        <div class="card__button">
          <button class="button-coral w-full">{{ $t('shared.book') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const { t } = useI18n()
const dayjs = useDayjs()
const route = useRoute()
const fat = useFatStore()
const date = computed(() => route.query.date || dayjs().format('YYYY-MM-DD'))

const props = defineProps({
  horizontal: {
    type: Boolean,
    default: false
  }, 
  position: {
    type: Number,
    default: 0
  },
  restaurant: {
    type: Object,
    default: () => {}
  },
  meal: {
    type: Object,
    default: () => {}
  }
})

// Dicount value
const discountValue = computed(() => {
  if (props.meal.altType === 3) return 30
  return 25
})

// Discount text
const discountText = computed(() => {
  if (props.meal.altType === 2) {
    return t('offers.meals.altType2.label', { discount: discountValue.value })
  }

  if (props.meal.altType === 3) {
    return t('offers.meals.altType3.label', { discount: discountValue.value })
  }

  return t('offers.meals.altType1.label', { discount: discountValue.value })
})

// Information text
const informationText = computed(() => {
  if (props.meal.altType === 1) {
    return t('offers.meals.altType1.info', { discount: discountValue.value })
  }

  if (props.meal.altType === 2) {
    return t('offers.meals.altType2.info', { discount: discountValue.value })
  }

  if (props.meal.altType === 3) {
    return t('offers.meals.altType3.info', { discount: discountValue.value })
  }

  return ''
})

// Get custom image or restaurant cover
const imageUrl = props.meal?.image || useCloudinaryImage(props.restaurant.cover, 600, 400)
</script>

<style lang="scss" scoped>
.card {
  $b: &;
  @apply @container;
  @apply w-full h-full cursor-pointer;

  //
  // Horizontal "row"
  //
  &--horizontal {
    #{$b}__wrapper {
      @apply flex items-center;
      @apply bg-mist rounded overflow-hidden;
    }

    #{$b}__media {
      @apply relative bg-black;
      @apply w-28 aspect-square;
    }

    #{$b}__media-overlay {
      @apply hidden;
    }

    #{$b}__main {
      @apply p-3 py-2 flex-1;
      @apply grid gap-1;
    }

    #{$b}__label {
      //
    }

    #{$b}__discount {
      @apply font-medium leading-tight text-2xs;
      @apply bg-ocean inline-block px-2 py-1 rounded text-white;
    }

    #{$b}__heading {
      @apply font-bold leading-tight;
    }

    #{$b}__description {
      @apply text-xs line-clamp-1; 
    }

    #{$b}__meta {
      @apply font-medium text-2xs mt-1;
      @apply @2xs:text-xs;
    }

    #{$b}__button {
      @apply hidden;
    }
  }
  
  //
  // Vertical card
  //
  &--vertical {
    #{$b}__wrapper {
      @apply flex flex-col @2xl:flex-row @2xl:items-center;
      @apply h-full bg-mist rounded overflow-hidden;
    }

    #{$b}__media {
      @apply relative bg-black;
      @apply @2xl:flex-1 @xl:min-w-[49.25%];
      @apply aspect-video;
      @apply @2xl:aspect-square @4xl:aspect-4/3;
    }

    #{$b}__promoted {
      @apply absolute z-10 top-2 left-2;
      @apply bg-sky text-ocean text-xs px-2 py-0.5 rounded-sm;
      @apply @2xs:text-sm @2xs:top-2 @2xs:left-2;
    }

    #{$b}__media-overlay {
      @apply absolute z-20 bottom-0 left-0 w-full; 
      @apply p-3 @xs:p-4 @2xl:p-6;
    }

    #{$b}__media-logo {
      @apply relative z-20 invert;
      @apply w-24 h-12 @xs:w-28 @xs:h-14 @2xl:w-32 @2xl:h-16;
    }

    #{$b}__media-gradient {
      @apply absolute z-10 bottom-0 left-0 w-full h-full bg-gradient-to-t from-25% from-black/80;
    }

    #{$b}__main {
      // @include boxer(red, 0, 1);
      // @include box();
      @apply flex-1;
      @apply p-3 @2xs:p-4 @xl:p-8;
      @apply flex flex-col gap-2;
    }

    #{$b}__label {
      // 
    }

    #{$b}__discount {
      @apply font-medium leading-tight text-xs;
      @apply bg-ocean inline-block px-2 py-1 rounded text-white;
      // @apply bg-sky text-ocean text-xs px-2 py-0.5 rounded-sm;
      // @apply @2xs:text-sm @2xs:top-2 @2xs:left-2;
    }

    #{$b}__heading {
      @apply font-bold leading-tight line-clamp-2;

      // 2up 
      @apply @2xs:font-medium @2xs:font-heading @2xs:text-2xl;

      // 1up
      @apply @2xl:text-4xl @4xl:text-5xl;
    }

    #{$b}__description {
      // @apply text-xs; 
      @apply text-sm;
      @apply @2xs:text-sm @4xl:text-base;
    }

    #{$b}__meta {
      @apply font-medium text-xs @2xs:text-sm;
      @apply mt-auto @2xl:mt-4;
    }

    #{$b}__button {
      @apply text-sm w-24 pt-2 mt-auto;
    }
  }
}
</style>